/**
 * Loose parsing of Timefold {@link ScoreAnalysis} JSON (varies by Timefold version / fetchPolicy).
 * Produces a constraint breakdown plus a per–timetable-card index for UI tooltips.
 */

const HARD_MEDIUM_SOFT_RE = /^(-?\d+)hard\/(-?\d+)medium\/(-?\d+)soft$/;

const MAX_CARD_FINDINGS_PER_CARD = 40;

/** Ready, human-readable constraint strings localized by the backend enrichment. */
export type LocalizedConstraint = {
  title: string;
  explanation: string;
  actionHint: string;
};

export type ParsedTimefoldConstraintRow = {
  id: string;
  label: string;
  hardScore: number;
  mediumScore: number;
  softScore: number;
  matchCount: number | null;
  detailSections: ParsedTimefoldMatchSection[];
  /** Backend-provided ready strings (title/explanation/actionHint); absent for map-only rows. */
  localized?: LocalizedConstraint;
};

/** Reads the backend enrichment's `localized` block off a raw constraint entry. */
function readLocalizedConstraint(entry: Record<string, unknown>): LocalizedConstraint | undefined {
  const raw = entry.localized;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.title !== 'string' || o.title.length === 0) {
    return undefined;
  }
  return {
    title: o.title,
    explanation: typeof o.explanation === 'string' ? o.explanation : '',
    actionHint: typeof o.actionHint === 'string' ? o.actionHint : '',
  };
}

export type ParsedTimefoldMatchSection = {
  matchScoreText?: string;
  lines: string[];
};

/** Strukturierte Zell-Referenz aus dem Backend-Enrichment (template TimeGridEntry × Wochentag). */
export type ParsedTimefoldSlotRef = {
  timegridEntryUuid: string;
  weekday: string;
};

export type ParsedTimefoldCardFinding = {
  constraintLabel: string;
  line: string;
  dedupeKey: string;
  slots?: ParsedTimefoldSlotRef[];
};

export type ParsedTimefoldAnalysis = {
  scoreText: string;
  hardTotal: number;
  mediumTotal: number;
  softTotal: number;
  constraints: ParsedTimefoldConstraintRow[];
  cardsByCardUuid: Record<string, ParsedTimefoldCardFinding[]>;
  cardsWithFindingsCount: number;
};

export type Score3 = { hard: number; medium: number; soft: number };

function parseHardMediumSoftString(s: string): Score3 | undefined {
  const m = s.trim().match(HARD_MEDIUM_SOFT_RE);
  if (!m) return undefined;
  return { hard: Number(m[1]), medium: Number(m[2]), soft: Number(m[3]) };
}

function parseScorePart(score: unknown): Score3 {
  const zero: Score3 = { hard: 0, medium: 0, soft: 0 };
  if (score === null || score === undefined) {
    return zero;
  }
  if (typeof score === 'string') {
    return parseHardMediumSoftString(score) ?? zero;
  }
  if (typeof score === 'object' && !Array.isArray(score)) {
    const o = score as Record<string, unknown>;
    if (typeof o.hard === 'number' && typeof o.medium === 'number' && typeof o.soft === 'number') {
      return { hard: o.hard, medium: o.medium, soft: o.soft };
    }
    if (typeof o.hardScore === 'number' && typeof o.mediumScore === 'number' && typeof o.softScore === 'number') {
      return { hard: o.hardScore, medium: o.mediumScore, soft: o.softScore };
    }
  }
  return zero;
}

function formatScoreText(hard: number, medium: number, soft: number): string {
  return `${hard}hard/${medium}medium/${soft}soft`;
}

function scoreToShortString(score: unknown): string | undefined {
  if (score === null || score === undefined) return undefined;
  if (typeof score === 'string') return score;
  const p = parseScorePart(score);
  return formatScoreText(p.hard, p.medium, p.soft);
}

const UUID_IN_STRING_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Matches a card finding's constraint label to a parsed constraint row label.
 * Timefold and {@link constraintMatchTotalMap} often disagree (short name vs {@code package/Name}).
 */
export function constraintLabelMatchesRow(findingLabel: string, rowLabel: string): boolean {
  const a = findingLabel.trim();
  const b = rowLabel.trim();
  if (a.length === 0 || b.length === 0) {
    return false;
  }
  if (a === b) {
    return true;
  }
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al === bl) {
    return true;
  }
  const tail = (s: string) => (s.includes('/') ? (s.split('/').pop() ?? s) : s).trim().toLowerCase();
  if (tail(a) === tail(b) && tail(a).length > 0) {
    return true;
  }
  if (al.endsWith(`/${bl}`) || bl.endsWith(`/${al}`)) {
    return true;
  }
  return false;
}

export function constraintLabelFromAnalysisEntry(entry: Record<string, unknown>): string {
  const ref = entry.constraintRef;
  if (ref && typeof ref === 'object' && !Array.isArray(ref)) {
    const r = ref as Record<string, unknown>;
    const name = r.constraintName;
    if (typeof name === 'string' && name.length > 0) return name;
    const pkg = r.constraintPackage;
    if (typeof pkg === 'string' && typeof name === 'string') {
      return `${pkg}/${name}`;
    }
  }
  if (typeof entry.constraintId === 'string') return entry.constraintId;
  if (typeof entry.constraintPackage === 'string' && typeof entry.constraintName === 'string') {
    return `${entry.constraintPackage}/${entry.constraintName}`;
  }
  if (typeof entry.constraintName === 'string') return entry.constraintName;
  return '—';
}

/**
 * Karten-UUIDs einer Justification, in absteigender Verlässlichkeit:
 * 1. `timetableCardUuids[]` (Backend-Enrichment — kann mehrere Karten benennen)
 * 2. `timetableCard.uuid` (explizite Karten-Referenz)
 * 3. direkte Karten-Felder (`timetableCardUuid`, `cardUuid`, …)
 * 4. `uuid` (Entity-UUID — im Analyze-Export identisch zur Karten-UUID)
 * 5. Regex über beliebige String-Werte (letzter Ausweg; kann falsche UUIDs treffen)
 */
function extractCardUuids(j: Record<string, unknown>): string[] {
  const enriched = j.timetableCardUuids;
  if (Array.isArray(enriched)) {
    const uuids = enriched.filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (uuids.length > 0) {
      return [...new Set(uuids)];
    }
  }
  const ref = j.timetableCard;
  if (ref && typeof ref === 'object' && !Array.isArray(ref)) {
    const tc = ref as Record<string, unknown>;
    if (typeof tc.uuid === 'string' && tc.uuid.length > 0) {
      return [tc.uuid];
    }
  }
  const directKeys = [
    'timetableCardUuid',
    'timetableCardId',
    'cardUuid',
    'planningCardUuid',
    'planningEntityUuid',
  ] as const;
  for (const k of directKeys) {
    const v = j[k];
    if (typeof v === 'string' && v.length > 0) {
      return [v];
    }
  }
  if (typeof j.uuid === 'string' && j.uuid.length >= 32) {
    return [j.uuid];
  }
  for (const v of Object.values(j)) {
    if (typeof v === 'string' && v.length >= 36) {
      const m = v.match(UUID_IN_STRING_RE);
      if (m?.[0]) {
        return [m[0]];
      }
    }
  }
  return [];
}

/** `slots` aus dem Backend-Enrichment der Justification (leer, wenn nicht vorhanden). */
function extractSlotRefs(j: Record<string, unknown>): ParsedTimefoldSlotRef[] {
  if (!Array.isArray(j.slots)) {
    return [];
  }
  const refs: ParsedTimefoldSlotRef[] = [];
  for (const slot of j.slots) {
    if (!slot || typeof slot !== 'object' || Array.isArray(slot)) continue;
    const s = slot as Record<string, unknown>;
    if (typeof s.timegridEntryUuid === 'string' && typeof s.weekday === 'string') {
      refs.push({ timegridEntryUuid: s.timegridEntryUuid, weekday: s.weekday });
    }
  }
  return refs;
}

function formatClassBits(j: Record<string, unknown>): string {
  const lessonClasses = j.lessonClasses;
  if (!Array.isArray(lessonClasses)) return '';
  const classBits: string[] = [];
  for (const lc of lessonClasses) {
    if (!lc || typeof lc !== 'object' || Array.isArray(lc)) continue;
    const o = lc as Record<string, unknown>;
    const sc = o.schoolClass;
    const div = o.division;
    let cname = '';
    if (sc && typeof sc === 'object' && !Array.isArray(sc)) {
      const name = (sc as Record<string, unknown>).name;
      if (typeof name === 'string') cname = name;
    }
    let dname = '';
    if (div && typeof div === 'object' && !Array.isArray(div)) {
      const name = (div as Record<string, unknown>).name;
      if (typeof name === 'string') dname = name;
    }
    if (cname) classBits.push(dname ? `${cname} (${dname})` : cname);
  }
  return classBits.join(', ');
}

function formatRoomBit(j: Record<string, unknown>): string {
  const roomSupply = j.roomSupply;
  if (
    Array.isArray(roomSupply) &&
    roomSupply[0] &&
    typeof roomSupply[0] === 'object' &&
    !Array.isArray(roomSupply[0])
  ) {
    const n = (roomSupply[0] as Record<string, unknown>).name;
    if (typeof n === 'string') return n;
  }
  return '';
}

/** Single readable line for tables / tooltips */
export function buildJustificationLine(constraintLabel: string, j: Record<string, unknown>): string {
  const bits: string[] = [];
  if (typeof j.subject === 'string' && j.subject.length > 0) bits.push(j.subject);
  const classes = formatClassBits(j);
  if (classes) bits.push(classes);
  const room = formatRoomBit(j);
  if (room) bits.push(room);
  const body = bits.filter(Boolean).join(' — ');
  return body ? `${constraintLabel}: ${body}` : constraintLabel;
}

function addCardFinding(
  cardsByCardUuid: Record<string, ParsedTimefoldCardFinding[]>,
  cardUuid: string,
  constraintLabel: string,
  line: string,
  slots: ParsedTimefoldSlotRef[],
) {
  const dedupeKey = `${constraintLabel}${line}`;
  const list = cardsByCardUuid[cardUuid] ?? [];
  if (list.some((f) => f.dedupeKey === dedupeKey)) return;
  if (list.length >= MAX_CARD_FINDINGS_PER_CARD) return;
  list.push({ constraintLabel, line, dedupeKey, ...(slots.length > 0 && { slots }) });
  cardsByCardUuid[cardUuid] = list;
}

function collectFromJustifications(
  constraintLabel: string,
  arr: unknown[],
  cardsByCardUuid: Record<string, ParsedTimefoldCardFinding[]>,
): string[] {
  const lines: string[] = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const j = item as Record<string, unknown>;
    const line = buildJustificationLine(constraintLabel, j);
    lines.push(line);
    const slots = extractSlotRefs(j);
    for (const uid of extractCardUuids(j)) {
      addCardFinding(cardsByCardUuid, uid, constraintLabel, line, slots);
    }
  }
  return lines;
}

function extractSectionsFromConstraintEntry(
  entry: Record<string, unknown>,
  constraintLabel: string,
  cardsByCardUuid: Record<string, ParsedTimefoldCardFinding[]>,
): ParsedTimefoldMatchSection[] {
  const sections: ParsedTimefoldMatchSection[] = [];

  if (Array.isArray(entry.matches)) {
    for (const m of entry.matches) {
      if (!m || typeof m !== 'object' || Array.isArray(m)) continue;
      const mm = m as Record<string, unknown>;
      const matchScoreText = scoreToShortString(mm.score);
      if (Array.isArray(mm.justification)) {
        const lines = collectFromJustifications(constraintLabel, mm.justification, cardsByCardUuid);
        if (lines.length > 0) {
          sections.push({ lines, matchScoreText });
        }
      }
    }
  }

  if (Array.isArray(entry.justification)) {
    const matchScoreText = scoreToShortString(entry.score ?? entry.weight);
    const lines = collectFromJustifications(constraintLabel, entry.justification, cardsByCardUuid);
    if (lines.length > 0) {
      sections.push({ lines, matchScoreText });
    }
  }

  return sections;
}

/** ScoreAnalysis wire response: top-level `constraints[]` with `id` + `matches[].justification` (not only `constraintAnalyses`). */
function isWireConstraintsArrayItem(entry: Record<string, unknown>): boolean {
  if (Array.isArray(entry.matches) || Array.isArray(entry.justification)) {
    return true;
  }
  if (
    typeof entry.id === 'string' &&
    entry.id.length > 0 &&
    (entry.score !== undefined || entry.weight !== undefined)
  ) {
    return true;
  }
  return entry.constraintName !== undefined || entry.constraintRef !== undefined;
}

function matchCountFromWireEntry(entry: Record<string, unknown>): number | null {
  if (Array.isArray(entry.matches)) {
    let justificationRows = 0;
    for (const m of entry.matches) {
      if (m && typeof m === 'object' && !Array.isArray(m)) {
        const just = (m as Record<string, unknown>).justification;
        if (Array.isArray(just)) {
          justificationRows += just.length;
        }
      }
    }
    if (justificationRows > 0) {
      return justificationRows;
    }
    return entry.matches.length;
  }
  if (Array.isArray(entry.justification)) {
    return entry.justification.length;
  }
  return null;
}

export function wireConstraintLabel(entry: Record<string, unknown>): string {
  if (typeof entry.id === 'string' && entry.id.trim().length > 0) {
    return entry.id.trim();
  }
  return constraintLabelFromAnalysisEntry(entry);
}

/**
 * Schneidet aus der rohen ScoreAnalysis genau die Einträge EINER Constraint-Regel heraus
 * (alle drei Quellformate), z. B. zum Kopieren/Teilen der Rohdaten eines einzelnen Findings.
 * @returns null, wenn die Regel in den Rohdaten nicht auffindbar ist.
 */
export function extractRawConstraintEntries(data: unknown, rowLabel: string): Record<string, unknown> | null {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  const root = data as Record<string, unknown>;
  const out: Record<string, unknown> = { constraint: rowLabel };
  let found = false;

  const fromMap = root.constraintMatchTotalMap;
  if (fromMap && typeof fromMap === 'object' && !Array.isArray(fromMap)) {
    const matching = Object.entries(fromMap).filter(([name]) => constraintLabelMatchesRow(name, rowLabel));
    if (matching.length > 0) {
      out.constraintMatchTotal = Object.fromEntries(matching);
      found = true;
    }
  }

  const analyses = root.constraintAnalyses;
  if (Array.isArray(analyses)) {
    const matching = analyses.filter(
      (item) =>
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        constraintLabelMatchesRow(constraintLabelFromAnalysisEntry(item as Record<string, unknown>), rowLabel),
    );
    if (matching.length > 0) {
      out.constraintAnalyses = matching;
      found = true;
    }
  }

  const constraintsWire = root.constraints;
  if (Array.isArray(constraintsWire)) {
    const matching = constraintsWire.filter(
      (item) =>
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        constraintLabelMatchesRow(wireConstraintLabel(item as Record<string, unknown>), rowLabel),
    );
    if (matching.length > 0) {
      out.constraints = matching;
      found = true;
    }
  }

  return found ? out : null;
}

type MutableRow = Omit<ParsedTimefoldConstraintRow, 'detailSections'> & {
  detailSections: ParsedTimefoldMatchSection[];
};

/**
 * @returns null if payload is not an object
 */
export function parseTimefoldScoreAnalysis(data: unknown): ParsedTimefoldAnalysis | null {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  const root = data as Record<string, unknown>;
  const total = parseScorePart(root.score);
  const scoreText = formatScoreText(total.hard, total.medium, total.soft);

  const rowByLabel = new Map<string, MutableRow>();
  const cardsByCardUuid: Record<string, ParsedTimefoldCardFinding[]> = {};

  const upsertRow = (label: string, partial: Partial<MutableRow> & { id: string }) => {
    const existing = rowByLabel.get(label);
    if (!existing) {
      rowByLabel.set(label, {
        id: partial.id,
        label,
        hardScore: partial.hardScore ?? 0,
        mediumScore: partial.mediumScore ?? 0,
        softScore: partial.softScore ?? 0,
        matchCount: partial.matchCount ?? null,
        detailSections: partial.detailSections ?? [],
        localized: partial.localized,
      });
    } else {
      if (partial.hardScore !== undefined) existing.hardScore = partial.hardScore;
      if (partial.mediumScore !== undefined) existing.mediumScore = partial.mediumScore;
      if (partial.softScore !== undefined) existing.softScore = partial.softScore;
      if (partial.matchCount !== undefined && partial.matchCount !== null) existing.matchCount = partial.matchCount;
      if (partial.localized && !existing.localized) existing.localized = partial.localized;
      if (partial.detailSections && partial.detailSections.length > 0) {
        // constraintAnalyses und Wire-constraints[] liefern oft dieselben Matches —
        // inhaltsgleiche Sections nur einmal aufnehmen
        const sectionKey = (s: ParsedTimefoldMatchSection) => `${s.matchScoreText ?? ''}${s.lines.join('')}`;
        const seen = new Set(existing.detailSections.map(sectionKey));
        const fresh = partial.detailSections.filter((s) => !seen.has(sectionKey(s)));
        if (fresh.length > 0) {
          existing.detailSections = [...existing.detailSections, ...fresh];
        }
      }
    }
  };

  const fromMap = root.constraintMatchTotalMap;
  if (fromMap && typeof fromMap === 'object' && !Array.isArray(fromMap)) {
    for (const [name, scoreVal] of Object.entries(fromMap)) {
      const s = parseScorePart(scoreVal);
      const id = `map:${name}`;
      upsertRow(name, {
        id,
        label: name,
        hardScore: s.hard,
        mediumScore: s.medium,
        softScore: s.soft,
        matchCount: null,
        detailSections: [],
      });
    }
  }

  const analyses = root.constraintAnalyses;
  if (Array.isArray(analyses)) {
    analyses.forEach((item, index) => {
      if (item === null || typeof item !== 'object' || Array.isArray(item)) return;
      const entry = item as Record<string, unknown>;
      const label = constraintLabelFromAnalysisEntry(entry);
      const s = parseScorePart(entry.score ?? entry.weight);
      const id = `ca:${label}:${index}`;
      const matchRaw = entry.matchCount;
      const matchCount =
        typeof matchRaw === 'number' ? matchRaw : matchRaw !== undefined && matchRaw !== null ? Number(matchRaw) : null;

      const detailSections = extractSectionsFromConstraintEntry(entry, label, cardsByCardUuid);

      upsertRow(label, {
        id,
        label,
        hardScore: s.hard,
        mediumScore: s.medium,
        softScore: s.soft,
        matchCount: Number.isFinite(matchCount as number) ? (matchCount as number) : null,
        detailSections,
        localized: readLocalizedConstraint(entry),
      });
    });
  }

  const constraintsWire = root.constraints;
  if (Array.isArray(constraintsWire)) {
    constraintsWire.forEach((item, index) => {
      if (item === null || typeof item !== 'object' || Array.isArray(item)) return;
      const entry = item as Record<string, unknown>;
      if (!isWireConstraintsArrayItem(entry)) {
        return;
      }
      const label = wireConstraintLabel(entry);
      const s = parseScorePart(entry.score ?? entry.weight);
      const rowId = `wire:${String(index)}:${label}`;
      const matchCount = matchCountFromWireEntry(entry);
      const detailSections = extractSectionsFromConstraintEntry(entry, label, cardsByCardUuid);
      upsertRow(label, {
        id: rowId,
        label,
        hardScore: s.hard,
        mediumScore: s.medium,
        softScore: s.soft,
        matchCount,
        detailSections,
        localized: readLocalizedConstraint(entry),
      });
    });
  }

  const rows: ParsedTimefoldConstraintRow[] = Array.from(rowByLabel.values()).map((r) => ({
    ...r,
    detailSections: r.detailSections,
  }));

  rows.sort((a, b) => {
    if (a.hardScore !== b.hardScore) return a.hardScore - b.hardScore;
    if (a.mediumScore !== b.mediumScore) return a.mediumScore - b.mediumScore;
    if (a.softScore !== b.softScore) return a.softScore - b.softScore;
    return a.label.localeCompare(b.label, 'de');
  });

  const cardsWithFindingsCount = Object.keys(cardsByCardUuid).length;

  return {
    scoreText,
    hardTotal: total.hard,
    mediumTotal: total.medium,
    softTotal: total.soft,
    constraints: rows,
    cardsByCardUuid,
    cardsWithFindingsCount,
  };
}

/**
 * Public re-export so wizard/diagnostics components can use the same parser
 * without each duplicating its own regex. Returns null for non-string input.
 */
export function parseTimefoldScoreString(s: unknown): Score3 | null {
  if (typeof s !== 'string') return null;
  return parseHardMediumSoftString(s) ?? null;
}
